/**
 * LYTES Electrolyte Finder v2 (sections/lytes-finder-v2.liquid).
 *
 * One routing question ("what are they mainly for?") sends each visitor down a short track,
 * then two questions everyone answers (sugar and sweeteners, Australian made). Most people
 * finish in five taps. Answers are scored against live product data from
 * /collections/electrolytes?view=quiz-data (templates/collection.quiz-data.liquid), so the
 * result always reflects current metafields, prices and stock.
 *
 * Rules carried over from v1 and kept on purpose:
 *  - every fact comes from a lytes.* metafield, never parsed from a description;
 *  - stevia and "no sweeteners" are hard exclusions, checked before any scoring;
 *  - popularity is a small tie-breaker (2 points at most) and never outranks a real need.
 * New in v2: out-of-stock and sugar-containing products (when zero sugar is asked for) are
 * excluded too, reasons quote real numbers, and the result can be shared, emailed and remembered.
 *
 * Results are deterministic for a given set of answers, so a shared link (?a=...) or a saved
 * result simply re-runs the scoring against today's data.
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-lytes-finder]');
  const CONFIG = root ? JSON.parse(root.querySelector('[data-finder-config]').textContent) : {};
  const QUIZ_VERSION = CONFIG.version || 'electrolyte-finder-v2';
  const STORAGE_KEY = 'lytes-finder';
  const BANDS = ['Low', 'Moderate', 'High', 'Very High'];

  // ─── Questions ──────────────────────────────────────────────────────────────
  // `use` options carry the track and the lytes.best_for tags they match. Each option's
  // `target` is a sodium band index (0 Low … 3 Very High); halves sit between two bands.
  const USES = {
    gym: { label: 'Gym and training', icon: 'gym', track: 'performance', short: 'training', tags: ['Gym and training'], related: ['Team sport', 'Recovery', 'Running, cycling or endurance'], phrase: 'You train regularly' },
    endurance: { label: 'Running, cycling and endurance', icon: 'runner', track: 'performance', short: 'endurance', tags: ['Running, cycling or endurance'], related: ['Gym and training', 'Team sport'], phrase: 'You run, ride or race' },
    heat: { label: 'Outdoor work, heat or long shifts', icon: 'sun', track: 'heat', short: 'long days in the heat', tags: ['Heavy sweating or outdoor work', 'Long shifts / FIFO / remote work'], related: ['Travel, hot weather or occasional hydration'], phrase: 'You spend long stretches in the heat' },
    travel: { label: 'Travel and the odd hot day', icon: 'plane', track: 'heat', short: 'travel and hot days', tags: ['Travel, hot weather or occasional hydration'], related: ['Everyday hydration'], phrase: 'You want something for travel and hot days' },
    keto: { label: 'Keto, low carb or fasting', icon: 'keto', track: 'keto', short: 'keto and fasting', tags: ['Keto, low-carb or fasting'], related: ['Everyday hydration'], phrase: 'You eat keto, low carb or fast' },
    everyday: { label: 'Everyday hydration', icon: 'drop', track: 'everyday', short: 'everyday use', tags: ['Everyday hydration', 'General wellness'], related: ['Travel, hot weather or occasional hydration'], phrase: 'You want an everyday electrolyte' },
  };

  const PRIORITIES = {
    max: { label: 'As many electrolytes as possible', sub: 'Around 1,000mg sodium per serve', phrase: 'put strength first', headline: 'high-sodium' },
    taste: { label: 'Great taste', sub: 'Flavours our customers rate highly', phrase: 'care most about taste', headline: 'top-rated' },
    value: { label: 'Lowest cost per serve', sub: 'Most serves for your money', phrase: 'care most about value', headline: 'best-value' },
    portable: { label: 'Easy to carry', sub: 'Single-serve sticks or tablets', phrase: 'need something easy to carry', headline: 'grab-and-go' },
    minerals: { label: 'A broad mineral mix', sub: 'Magnesium, potassium and trace minerals', phrase: 'care about a broad mineral mix', headline: 'full-mineral' },
    simple: { label: 'Simple ingredients', sub: 'No artificial sweeteners, short labels', phrase: 'prefer simple ingredients', headline: 'simple-ingredient' },
    au: { label: 'Australian made', sub: 'Made here or with Australian ingredients', phrase: 'want Australian made', headline: 'Australian' },
  };
  // "What matters most" is ranked: up to three picks, the first counts most
  const RANK_WEIGHTS = [1, 0.6, 0.35];
  const MAX_PRIORITIES = 3;
  const ranksOf = (a) => String(a.priority || '').split('.').filter((k, i, arr) => PRIORITIES[k] && arr.indexOf(k) === i).slice(0, MAX_PRIORITIES);

  const QUESTIONS = {
    use: {
      stage: 1,
      title: 'What are electrolytes mainly for?',
      layout: 'icons',
      options: () => Object.entries(USES).map(([value, u]) => ({ value, label: u.label, icon: u.icon })),
    },
    sweat: {
      stage: 1,
      title: 'How much do you sweat when you train?',
      hint: 'The more you sweat, the more sodium you lose, so this sets how strong a formula we look for.',
      options: () => [
        { value: 'light', label: 'A light sweat', sub: 'Shorter or easier sessions', target: 1, phrase: 'with a light sweat' },
        { value: 'steady', label: 'A steady sweat', sub: 'Damp shirt by the end', target: 2, phrase: 'and sweat steadily' },
        { value: 'heavy', label: 'Drenched', sub: 'Soaked shirt, long or hot sessions', target: 3, phrase: 'and finish drenched' },
        { value: 'salty', label: 'Salt marks on my kit', sub: 'White streaks on clothes or a cap', target: 3, phrase: 'and leave salt marks on your kit' },
        { value: 'unsure', label: 'Not sure', target: 2, phrase: '' },
      ],
    },
    exposure: {
      stage: 1,
      title: 'How long are you usually out in the heat?',
      hint: 'Time in the heat is the best guide to how much you sweat, so this sets how strong a formula we look for.',
      // Travel picks skip this question: an hour or two at a time is assumed and can be changed
      skipIf: (a) => a.use === 'travel',
      infer: (a) => (a.use === 'travel' ? 'short' : null),
      options: () => [
        { value: 'short', label: 'An hour or two', sub: 'A hot commute, a flight, a day out', target: 1, phrase: 'for an hour or two at a time' },
        { value: 'half', label: 'Half a day', sub: 'Weekend jobs, sport on a hot day', target: 2, phrase: 'for half a day at a time' },
        { value: 'full', label: 'A full shift or longer', sub: 'Outdoor work, FIFO, long hot days', target: 3, phrase: 'for full shifts or longer' },
        { value: 'varies', label: 'It changes a lot', target: 2, phrase: '' },
      ],
    },
    keto: {
      stage: 1,
      title: 'When will you mostly have them?',
      hint: 'This sets how strong a formula we look for.',
      options: () => [
        { value: 'daily', label: 'Every day, in my water', target: 2.5, phrase: 'and have electrolytes every day' },
        { value: 'fasting', label: 'During fasts', target: 2.5, phrase: 'and fast' },
        { value: 'training', label: 'Around training too', target: 3, phrase: 'and train as well' },
        { value: 'starting', label: 'Just starting out', sub: 'Still finding my feet', target: 2, phrase: 'and are just starting out' },
      ],
    },
    taste: {
      stage: 1,
      title: 'How do you like it to taste?',
      options: () => [
        { value: 'fruity', label: 'Fruity and full flavoured', target: 1, phrase: 'with plenty of flavour' },
        { value: 'light', label: 'Light and easy to drink', target: 0.5, phrase: 'that is light and easy to drink' },
        { value: 'salty', label: 'Salty is fine', sub: 'I want the stronger stuff', target: 2.5, phrase: 'and don’t mind salty' },
        { value: 'plain', label: 'Unflavoured', sub: 'I add it to water or food', target: 1.5, phrase: 'that’s unflavoured' },
        { value: 'any', label: 'No preference', target: 1.5, phrase: '' },
      ],
    },
    sweet: {
      stage: 2,
      title: 'Sugar and sweeteners: what suits you?',
      hint: 'We leave out anything that doesn’t fit, so you won’t be matched with stevia if you’d rather avoid it.',
      inferredNote: (a) => (USES[a.use] && USES[a.use].track === 'keto' ? 'Zero sugar is already set because you chose keto.' : ''),
      options: (a) => {
        const keto = USES[a.use] && USES[a.use].track === 'keto';
        const list = [
          { value: 'zero', label: 'Zero sugar, any sweetener is fine', sub: 'Stevia, monk fruit and others', phrase: 'want zero sugar' },
          { value: 'no-stevia', label: 'Zero sugar, but no stevia', phrase: 'want zero sugar without stevia' },
          { value: 'nothing', label: 'Nothing sweet at all', sub: 'No sugar and no sweeteners', phrase: 'want nothing sweet at all' },
        ];
        if (!keto) {
          list.push({ value: 'some', label: 'A little sugar is fine', phrase: 'don’t mind a little sugar' });
          list.push({ value: 'any', label: 'No preference', phrase: '' });
        }
        return list;
      },
    },
    priority: {
      stage: 2,
      multi: true,
      title: 'What matters most to you?',
      hint: 'Pick up to three, most important first. Tap again to remove one.',
      // Taste, cost and Australian made are on every track; the rest depend on what it's for
      options: (a) => {
        const sets = {
          performance: ['max', 'taste', 'value', 'portable', 'au'],
          heat: ['portable', 'max', 'taste', 'value', 'au'],
          keto: ['minerals', 'simple', 'taste', 'value', 'max', 'au'],
          everyday: ['taste', 'value', 'simple', 'minerals', 'au'],
        };
        const track = (USES[a.use] || USES.everyday).track;
        return sets[track].map((k) => ({ value: k, label: PRIORITIES[k].label, sub: PRIORITIES[k].sub }));
      },
    },
  };

  const TRACK_QUESTION = { performance: 'sweat', heat: 'exposure', keto: 'keto', everyday: 'taste' };

  function pathFor(a) {
    const track = USES[a.use] ? USES[a.use].track : null;
    const path = ['use'];
    if (track) path.push(TRACK_QUESTION[track]);
    path.push('sweet', 'priority');
    return path.filter((id) => !(QUESTIONS[id].skipIf && QUESTIONS[id].skipIf(a)));
  }

  function optionOf(id, a) {
    const q = QUESTIONS[id];
    if (q.multi) {
      // A ranked answer is valid when every pick is one of this track's options
      const values = q.options(a).map((o) => o.value);
      const picks = ranksOf(a);
      return picks.length && picks.every((k) => values.includes(k)) ? { value: a[id], picks } : null;
    }
    return q.options(a).find((o) => o.value === a[id]) || null;
  }

  // ─── Product helpers ────────────────────────────────────────────────────────
  const has = (arr, v) => Array.isArray(arr) && arr.indexOf(v) !== -1;
  const bandIndex = (p) => BANDS.indexOf(p.sodium_band);
  const perServe = (p) => (p.price && p.serves > 0 ? p.price / p.serves : p.price_per_serve_stored || null);
  const isUnflavoured = (p) => /unflavou?red|\braw\b|plain/i.test(p.title) || p.flavour === 'Unflavoured';
  // Nothing added to sweeten it: lytes.no_sweeteners, or a sweeteners list of just "None"
  const unsweetened = (p) => p.no_sweeteners === true || (Array.isArray(p.sweeteners) && p.sweeteners.length === 1 && p.sweeteners[0] === 'None');
  const isFlavoured = (p) => !isUnflavoured(p) && !unsweetened(p);
  const auStatus = (p) => {
    if (p.australian_made === true || p.made_in === 'Australia') return 'made';
    if (p.australian_ingredients === true) return 'ingredients';
    if (p.brand_country === 'Australia') return 'brand';
    return null;
  };
  const AU_LABEL = { made: 'Made in Australia', ingredients: 'Made with Australian ingredients', brand: 'An Australian brand' };
  const FORMAT_NOUN = { 'Stick / Sachet': 'sticks', 'Capsule / Tablet': 'capsules', 'Liquid Drops': 'serves of drops', 'Tub / Powder': 'serves', 'Bulk Pouch': 'serves', 'Ready-to-Drink Bottle': 'bottles' };

  // ─── Product lines ──────────────────────────────────────────────────────────
  // The result recommends a product line (Re-Lyte Hydration, LMNT), never one flavour: a flavour
  // the shopper doesn't like would lose them. A line is one brand's flavours and pack sizes of the
  // same formula: same sodium, sugar profile and sweeteners (so Re-Lyte Hydration tubs and sticks
  // are one line, and the unsweetened Re-Lyte is its own).
  // Names for lines whose titles don't share a clean prefix (Re-Lyte titles mix "Re-Lyte" and "ReLyte")
  const LINE_NAMES = { 'Sodii|1000': 'Sodii Everyday Hydration Salts', 'Redmond|810': 'Re-Lyte Hydration', 'Redmond|1000': 'Re-Lyte Hydration Unflavoured' };
  const LINE_FORMAT_NAME = { 'Stick / Sachet': 'Electrolyte Sticks', 'Tub / Powder': 'Electrolyte Powder', 'Capsule / Tablet': 'Electrolyte Capsules', 'Liquid Drops': 'Electrolyte Drops', 'Bulk Pouch': 'Electrolyte Powder', 'Ready-to-Drink Bottle': 'Electrolyte Drinks' };
  const PACK_RE = /\b(variety|sampler|taster|big fruity|favourites|medley|bundle)\b/i;
  const SIZE_RE = /\d|\b(tub|tubs|sticks?|sachets?|serves?|ct|count|caps|capsules?|tablets?|ml|g|kg|pouch|bulk|bottles?|single|jar|pack|packs)\b/i;
  // Title words that name the product, not a flavour
  const NOT_FLAVOUR = /^(hydrate|hydration|essential|concentrate|concentrace|extra strength.*|shots?|fastchews|buffered|keto|keto drops|drops?|original|complete|plus|salty|electrolytes?|ocean trace|athlete.*)$/i;
  const squash = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
  const titleCase = (s) => (s === s.toUpperCase() ? s.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase()) : s);
  const lineKey = (p) => [p.vendor, p.sodium_mg, p.sugar_profile, (p.sweeteners || []).slice().sort().join('+')].join('|');
  const normTitle = (t) => String(t || '').replace(/\s*[-–]\s+|\s+[-–]\s*/g, ' - ').replace(/\s+/g, ' ').trim();
  const escRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const listJoin = (arr, word = 'or') => (arr.length <= 1 ? arr.join('') : arr.slice(0, -1).join(', ') + ` ${word} ` + arr[arr.length - 1]);

  function lineName(items) {
    const first = items[0];
    if (LINE_NAMES[first.vendor + '|' + first.sodium_mg]) return LINE_NAMES[first.vendor + '|' + first.sodium_mg];
    let name;
    if (items.length === 1) {
      name = normTitle(first.title).split(' - ')[0];
    } else {
      // Words every title in the line starts with ("Re-Lyte Hydration", "Hydrate Perform")
      const words = items.map((p) => normTitle(p.title).split(' '));
      const common = [];
      for (let i = 0; i < words[0].length && words.every((w) => w[i] && w[i].toLowerCase() === words[0][i].toLowerCase()); i++) common.push(words[0][i]);
      name = common.join(' ');
    }
    name = name.replace(/\s+-\s+/g, ' ').replace(/\s*\d+(\.\d+)?\s*(ml|g|kg)\b/gi, '').replace(/[\s-]+$/, '').trim();
    if (name.split(' ').length < 2 || PACK_RE.test(name) || /^salty\b/i.test(name)) {
      const formats = [...new Set(items.map((p) => p.product_format))];
      name = first.vendor + ' ' + (formats.length > 1 ? 'Electrolytes' : LINE_FORMAT_NAME[formats[0]] || 'Electrolytes');
    }
    return name;
  }

  // The flavour of one product in a line, or 'pack' for a variety pack, or '' when the title has none
  function flavourOf(p, name, single) {
    if (p.flavour) return titleCase(p.flavour);
    if (PACK_RE.test(p.title)) return 'pack';
    // A one-product line only names a flavour when the flavour field has one
    if (single) return '';
    // Leading words that belong to the brand or line name ("Re-Lyte", "ReLyte", "Hydrate Perform")
    const own = new Set([...String(name).split(/\s+/), ...String(p.vendor).split(/\s+/), 'relyte'].map(squash));
    for (let seg of normTitle(p.title).split(' - ')) {
      const words = seg.split(' ');
      while (words.length && own.has(squash(words[0]))) words.shift();
      seg = words.join(' ').replace(/\b(electrolytes?|hydration|drink|mix|powder|perform|jar|drops)\b/gi, '').replace(/\bsalt\s*$/i, '');
      seg = seg.replace(/\s*\d+(\.\d+)?\s*(ml|g|kg|l|ct)\b/gi, '').replace(/\s+/g, ' ').trim();
      if (seg && !SIZE_RE.test(seg) && !NOT_FLAVOUR.test(seg)) return titleCase(seg);
    }
    return '';
  }

  function packSize(p) {
    if (!p.serves) return '';
    return { 'Stick / Sachet': `${p.serves} sticks`, 'Tub / Powder': `tub, ${p.serves} serves`, 'Capsule / Tablet': `${p.serves} capsules`, 'Bulk Pouch': `pouch, ${p.serves} serves`, 'Liquid Drops': `${p.serves} serves`, 'Ready-to-Drink Bottle': `${p.serves} bottles` }[p.product_format] || `${p.serves} serves`;
  }

  function describeLine(items, best) {
    const name = lineName(items);
    const sorted = items.slice().sort((x, y) => (PACK_RE.test(y.title) - PACK_RE.test(x.title)) || (Number(y.reviews) || 0) - (Number(x.reviews) || 0));
    const flavours = new Map();
    let packs = 0;
    const options = sorted.map((p) => {
      const f = flavourOf(p, name, items.length === 1);
      if (f === 'pack') packs++;
      else if (f) flavours.set(f.toLowerCase(), f);
      const label = f === 'pack' ? normTitle(p.title).split(' - ')[0].replace(new RegExp('^' + escRe(p.vendor) + '\\s+', 'i'), '') : f || normTitle(p.title).split(' - ')[0];
      return { p, label, size: packSize(p), pack: f === 'pack' };
    });
    const sizes = {};
    for (const p of items) if (p.serves) (sizes[p.product_format] = sizes[p.product_format] || new Set()).add(p.serves);
    const nums = (set) => listJoin([...set].sort((x, y) => x - y).map(String));
    const formats = Object.entries(sizes).map(([f, set]) => {
      if (f === 'Stick / Sachet') {
        const multi = [...set].filter((n) => n > 1);
        const single = set.has(1) ? 'single sticks' : '';
        return multi.length ? `sticks (${single ? single + ' or ' : ''}packs of ${nums(new Set(multi))})` : single;
      }
      if (f === 'Tub / Powder' || f === 'Bulk Pouch') return `${f === 'Bulk Pouch' ? 'bulk pouches' : 'tubs'} (${nums(set)} serves)`;
      if (f === 'Capsule / Tablet') return `capsules (${nums(set)})`;
      if (f === 'Liquid Drops') return 'drops';
      if (f === 'Ready-to-Drink Bottle') return 'ready to drink bottles';
      return '';
    }).filter(Boolean);
    const pps = items.map(perServe).filter(Boolean);
    const rated = items.filter((p) => Number(p.rating) > 0 && Number(p.reviews) > 0);
    const reviews = rated.reduce((n, p) => n + Number(p.reviews), 0);
    const ratingAvg = reviews ? rated.reduce((n, p) => n + Number(p.rating) * Number(p.reviews), 0) / reviews : 0;
    const packImage = sorted.find((p) => PACK_RE.test(p.title) && p.image);
    return {
      name,
      vendor: best.vendor,
      flavours: [...flavours.values()],
      packs,
      formats,
      options,
      minPps: pps.length ? Math.min(...pps) : null,
      minPrice: Math.min(...items.map((p) => p.price).filter((n) => typeof n === 'number')),
      rating: ratingAvg,
      reviews,
      image: (packImage || best).image,
      url: best.brand_url || best.url,
    };
  }

  const money = (n) => (typeof n === 'number' && isFinite(n) ? '$' + n.toFixed(2) : '');
  const mg = (n) => (typeof n === 'number' ? n.toLocaleString('en-AU') + 'mg' : '—');
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // ─── Scoring ────────────────────────────────────────────────────────────────
  function sodiumTarget(a) {
    const track = USES[a.use] ? USES[a.use].track : 'everyday';
    const opt = optionOf(TRACK_QUESTION[track], a) || (track === 'heat' ? { target: 1 } : null);
    let target = opt && typeof opt.target === 'number' ? opt.target : 1.5;
    // "As many electrolytes as possible" in the first two picks raises the sodium we look for
    if (ranksOf(a).slice(0, 2).includes('max')) target = Math.max(target, 2.5);
    return target;
  }

  function exclusion(p, a) {
    if (!p.available) return 'Out of stock';
    if (a.sweet === 'no-stevia' && has(p.sweeteners, 'Stevia')) return 'Contains stevia';
    if (a.sweet === 'nothing' && !unsweetened(p)) return 'Contains a sweetener';
    if (zeroSugarWanted(a) && p.sugar_profile === 'Contains sugar') return 'Contains sugar';
    return '';
  }

  function zeroSugarWanted(a) {
    return a.sweet === 'zero' || a.sweet === 'no-stevia' || a.sweet === 'nothing' || (USES[a.use] && USES[a.use].track === 'keto');
  }

  function score(p, a, field) {
    const s = { use: 0, sodium: 0, sugar: 0, priority: 0, taste: 0, au: 0, popularity: 0 };
    const u = USES[a.use];

    if (u) {
      if (u.tags.some((t) => has(p.best_for, t))) s.use = 5;
      else if (u.related.some((t) => has(p.best_for, t))) s.use = 2;
    }

    const bi = bandIndex(p);
    if (bi >= 0) s.sodium = Math.max(-2, 4 - 2 * Math.abs(bi - sodiumTarget(a)));

    if (zeroSugarWanted(a)) s.sugar = p.sugar_profile === 'Zero sugar' ? 3 : 1;
    else s.sugar = 1;

    // Ranked priorities: each pick's 0 to 4 (Australian made 0 to 5) score, weighted 1, 0.6, 0.35
    s.priority = ranksOf(a).reduce((sum, k, i) => sum + RANK_WEIGHTS[i] * priorityScore(p, k, field), 0);

    // Everyday track: taste style (salty and light are handled by the sodium target)
    if (a.taste === 'fruity') s.taste = isFlavoured(p) ? 3 : 0;
    if (a.taste === 'plain') s.taste = !isFlavoured(p) ? 4 : -2;
    if (a.use === 'travel' && (p.product_format === 'Stick / Sachet' || p.product_format === 'Capsule / Tablet')) s.taste += 1;

    s.popularity = { 'Top 10%': 2, 'Top 25%': 1 }[p.popularity_tier] || 0;
    if (Number(p.rating) >= 4.5 && p.reviews >= 20) s.popularity += 0.5;

    let total = 0;
    for (const k in s) total += s[k];
    return { total, s };
  }

  function priorityScore(p, priority, field) {
    switch (priority) {
      case 'max':
        return p.sodium_mg >= 1000 ? 4 : p.sodium_mg >= 700 ? 3 : p.sodium_mg >= 500 ? 1 : 0;
      case 'taste': {
        const r = Number(p.rating) || 0;
        // Unflavoured and unsweetened mixes are made to be neutral, so they never win on taste
        if (!isFlavoured(p)) return 0;
        return r >= 4.8 && p.reviews >= 10 ? 4 : r >= 4.6 && p.reviews >= 5 ? 3 : r >= 4.3 && p.reviews >= 3 ? 1 : 0;
      }
      case 'value': {
        const pps = perServe(p);
        if (!pps || !field.pps.length) return 0;
        const rank = field.pps.filter((x) => x < pps).length / field.pps.length;
        return rank <= 0.25 ? 4 : rank <= 0.5 ? 2 : rank <= 0.75 ? 1 : 0;
      }
      case 'portable': {
        const fmt = p.product_format;
        let pts = fmt === 'Stick / Sachet' || fmt === 'Capsule / Tablet' || fmt === 'Ready-to-Drink Bottle' ? 3 : 0;
        if (p.portable) pts += 1;
        return Math.min(pts, 4);
      }
      case 'minerals':
        return (p.trace_minerals ? 2 : 0) + (p.magnesium_mg >= 50 ? 1 : 0) + ((p.mineral_profile || []).length >= 4 ? 1 : 0);
      case 'simple':
        return (p.no_artificial_sweeteners ? 2 : 0) + ((p.sweeteners || []).length <= 1 ? 1 : 0) + (unsweetened(p) ? 1 : 0);
      case 'au':
        // Only where the label or the brand confirms it; the strongest single criterion when ranked first
        return { made: 5, ingredients: 4.5, brand: 4 }[auStatus(p)] || 0;
      default:
        return 0;
    }
  }

  // The latest ranking's field (per-serve prices that fit), used by headline() for the value pick
  let lastField = null;

  function rank(products, a) {
    const pool = [];
    const excluded = [];
    for (const p of products) {
      const why = exclusion(p, a);
      if (why) excluded.push({ p, why });
      else pool.push(p);
    }
    const field = { pps: pool.map(perServe).filter(Boolean).sort((x, y) => x - y), sodium: pool.map((p) => p.sodium_mg || 0) };
    lastField = field;
    const scored = pool.map((p) => ({ p, ...score(p, a, field) })).sort((x, y) => y.total - x.total || (Number(y.p.rating) || 0) - (Number(x.p.rating) || 0));
    // Group into product lines: a line scores as its best-fitting product and lists every in-stock
    // flavour and pack size that fits the answers. One line per brand on the result screen.
    const lines = new Map();
    for (const x of scored) {
      const k = lineKey(x.p);
      if (!lines.has(k)) lines.set(k, { best: x, items: [] });
      lines.get(k).items.push(x.p);
    }
    const seen = new Set();
    const byBrand = [...lines.values()]
      .filter((l) => (seen.has(l.best.p.vendor) ? false : seen.add(l.best.p.vendor)))
      .map((l) => ({ ...l.best, line: describeLine(l.items, l.best.p) }));
    return { byBrand, scored, excluded, field, poolSize: pool.length };
  }

  // ─── Words: headline, summary, reasons ──────────────────────────────────────
  function bandRanges(products) {
    const r = {};
    for (const p of products) {
      if (typeof p.sodium_mg !== 'number' || !p.sodium_band) continue;
      const x = (r[p.sodium_band] = r[p.sodium_band] || { min: Infinity, max: 0 });
      x.min = Math.min(x.min, p.sodium_mg);
      x.max = Math.max(x.max, p.sodium_mg);
    }
    return r;
  }

  function round50(n) {
    return Math.round(n / 50) * 50;
  }

  function bandPhrase(target, ranges) {
    const band = BANDS[Math.round(Math.min(3, Math.max(0, target)))];
    const r = ranges[band];
    const span = !r ? '' : r.max - r.min <= 100 ? ` (about ${round50(r.max).toLocaleString('en-AU')}mg sodium per serve)` : band === 'Low' ? ` (under ${round50(r.max + 49)}mg sodium per serve)` : ` (about ${round50(r.min)} to ${round50(r.max).toLocaleString('en-AU')}mg sodium per serve)`;
    const words = { Low: 'a lighter', Moderate: 'a moderate sodium', High: 'a high sodium', 'Very High': 'a very high sodium' }[band];
    return words + ' formula' + span;
  }

  function summary(a, ranges) {
    const u = USES[a.use];
    const track = u.track;
    const detailId = TRACK_QUESTION[track];
    // A skipped question (travel skips time in the heat) adds nothing to the sentence
    const detail = QUESTIONS[detailId].skipIf && QUESTIONS[detailId].skipIf(a) ? null : optionOf(detailId, a);
    const sweet = optionOf('sweet', a);
    const parts = [];
    let first = u.phrase;
    if (detail && detail.phrase) first += ' ' + detail.phrase;
    parts.push(first);
    if (sweet && sweet.phrase) parts.push(sweet.phrase);
    const picks = ranksOf(a);
    if (picks.length) parts.push(PRIORITIES[picks[0]].phrase + (picks[1] ? ', then ' + PRIORITIES[picks[1]].label.toLowerCase() : ''));
    const lead = parts.length > 2 ? parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1] : parts.join(' and ');

    let fit = bandPhrase(sodiumTarget(a), ranges);
    if (a.sweet === 'no-stevia') fit += ' with zero sugar and no stevia';
    else if (a.sweet === 'nothing') fit += ' with no sugar and no sweeteners';
    else if (zeroSugarWanted(a)) fit += ' with zero sugar';
    return `${lead}. That points to ${fit}.`;
  }

  function headline(top, a) {
    const u = USES[a.use];
    // Lead with the first pick the match is strong on
    const strong = ranksOf(a).find((k) => priorityScore(top.p, k, lastField || { pps: [] }) >= 3);
    let attr = '';
    if (strong) attr = PRIORITIES[strong].headline;
    else if (a.sweet === 'nothing' && unsweetened(top.p)) attr = 'sweetener-free';
    else if (zeroSugarWanted(a) && top.p.sugar_profile === 'Zero sugar') attr = 'zero-sugar';
    return attr ? `Your ${attr} match for ${u.short}` : `Your match for ${u.short}`;
  }

  function reasons(x, a, field) {
    const p = x.p;
    const u = USES[a.use];
    const out = [];
    const picks = ranksOf(a);
    // Prices quote the line's lowest per-serve price, since the shopper picks the flavour and pack
    const pps = (x.line && x.line.minPps) || perServe(p);

    if (x.s.sodium >= 3 && typeof p.sodium_mg === 'number') {
      const pct = Math.round((field.sodium.filter((n) => n < p.sodium_mg).length / field.sodium.length) * 100);
      out.push(`${mg(p.sodium_mg)} sodium per serve${pct >= 60 ? `, more sodium than ${pct}% of the products that fit you` : `, in the ${String(p.sodium_band).toLowerCase()} range your answers point to`}`);
    }
    if (a.sweet === 'nothing' && unsweetened(p)) out.push('No sugar and no sweeteners');
    else if (a.sweet === 'no-stevia') out.push(unsweetened(p) ? 'Zero sugar and no sweeteners at all' : `Zero sugar, sweetened with ${(p.sweeteners || []).filter((s) => s !== 'None').join(' and ').toLowerCase() || 'no stevia'}`);
    else if (zeroSugarWanted(a) && p.sugar_profile === 'Zero sugar') out.push('Zero sugar');
    else if (zeroSugarWanted(a) && typeof p.sugar_g === 'number') out.push(`Very low sugar, ${p.sugar_g}g per serve`);

    if (picks.includes('value') && pps) {
      const cheaper = Math.round((field.pps.filter((n) => n > pps).length / field.pps.length) * 100);
      out.push(`From ${money(pps)} per serve${cheaper >= 50 ? `, cheaper than ${cheaper}% of the products that fit you` : ''}`);
    }
    if (picks.includes('portable') && priorityScore(p, 'portable', field) >= 3) out.push(`Comes in single-serve ${FORMAT_NOUN[p.product_format] || 'serves'}, easy to carry`);
    if (picks.includes('minerals') && priorityScore(p, 'minerals', field) >= 2) {
      const bits = [];
      if (p.potassium_mg) bits.push(`${mg(p.potassium_mg)} potassium`);
      if (p.magnesium_mg) bits.push(`${mg(p.magnesium_mg)} magnesium`);
      if (p.trace_minerals) bits.push('trace minerals');
      if (bits.length) out.push('Also has ' + bits.join(', '));
    }
    // Skip it when the sugar and sweetener reason already says there are none
    if (picks.includes('simple') && p.no_artificial_sweeteners && !(unsweetened(p) && (a.sweet === 'nothing' || a.sweet === 'no-stevia'))) out.push(unsweetened(p) ? 'No sweeteners of any kind' : 'No artificial sweeteners');
    const lr = x.line && x.line.reviews >= 5 ? x.line : Number(p.reviews) >= 5 ? { rating: Number(p.rating), reviews: p.reviews } : null;
    if ((picks.includes('taste') || a.taste === 'fruity') && lr && lr.rating >= 4.3) out.push(`Rated ${lr.rating.toFixed(1)} out of 5 from ${lr.reviews} reviews`);
    if (a.taste === 'plain' && x.s.taste > 0) out.push('Unflavoured, so it goes into water or food');
    if (picks.includes('au') && auStatus(p)) out.push(AU_LABEL[auStatus(p)]);
    if (x.s.use === 5) out.push(`One of our picks for ${u.tags.filter((t) => has(p.best_for, t))[0].toLowerCase()}`);
    if (!out.length && pps) out.push(`${money(pps)} per serve`);
    return out.slice(0, 4);
  }

  function tradeoff(alt, top, a) {
    const ap = (alt.line && alt.line.minPps) || perServe(alt.p);
    const tp = (top.line && top.line.minPps) || perServe(top.p);
    if (ap && tp && ap < tp - 0.05) return `Cheaper: from ${money(ap)} per serve`;
    if (alt.p.sodium_mg > top.p.sodium_mg) return `Stronger: ${mg(alt.p.sodium_mg)} sodium per serve`;
    if (alt.p.sodium_mg < top.p.sodium_mg && !ranksOf(a).includes('max')) return `Lighter: ${mg(alt.p.sodium_mg)} sodium per serve`;
    if (auStatus(alt.p) && !auStatus(top.p)) return AU_LABEL[auStatus(alt.p)];
    if (Number(alt.p.rating) > Number(top.p.rating || 0) && alt.p.reviews >= 5) return `Rated ${Number(alt.p.rating).toFixed(1)} from ${alt.p.reviews} reviews`;
    if (alt.p.product_format !== top.p.product_format) return `Comes as ${String(alt.p.product_format).toLowerCase()}`;
    return 'A close alternative';
  }

  // ─── Data ───────────────────────────────────────────────────────────────────
  let dataPromise = null;
  function fetchFeed(url) {
    // No JSON Accept header: with one, Shopify answers with its own collection JSON instead of the view
    return fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then((d) => {
        if (!d || !Array.isArray(d.products)) throw new Error('Unexpected product data');
        return d.products;
      });
  }
  function loadProducts() {
    if (!dataPromise) {
      // A cached copy of the wrong response can sit on the CDN briefly; one fresh request gets past it
      dataPromise = fetchFeed(CONFIG.data_url)
        .catch(() => fetchFeed(CONFIG.data_url + '&fresh=' + Date.now()))
        .catch((e) => {
          dataPromise = null;
          throw e;
        });
    }
    return dataPromise;
  }

  // ─── State, analytics, storage ──────────────────────────────────────────────
  let answers = {};
  let current = 0;
  let lastResult = null;
  const sessionId = 'eq_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);

  function track(name, detail = {}) {
    detail.session_id = sessionId;
    detail.quiz_version = QUIZ_VERSION;
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) window.Shopify.analytics.publish(name, detail);
    } catch (e) {}
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  const store = {
    get() {
      try {
        const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        return v && v.v === 2 && v.answers ? v : null;
      } catch (e) {
        return null;
      }
    },
    set(v) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
      } catch (e) {}
    },
  };

  function encodeAnswers(a) {
    return pathFor(a)
      .filter((id) => a[id])
      .map((id) => id + ':' + a[id])
      .join(',');
  }

  function decodeAnswers(str) {
    const a = {};
    let au = '';
    String(str || '')
      .split(',')
      .forEach((pair) => {
        const [k, v] = pair.split(':');
        if (k === 'au') au = v;
        else if (QUESTIONS[k] && v) a[k] = v;
      });
    // Links and saved results from before 24 Oct 2026 had a separate Australian made question
    // and one priority: "A lot" becomes the first pick, "Nice to have" the last
    if (au === 'must' || au === 'nice') {
      const picks = ranksOf(a).filter((k) => k !== 'au');
      if (au === 'must') picks.unshift('au');
      else picks.push('au');
      a.priority = picks.slice(0, MAX_PRIORITIES).join('.');
    }
    // Fill skipped questions with their inferred answer, then check every answer is a real option
    Object.keys(QUESTIONS).forEach((id) => {
      const q = QUESTIONS[id];
      if (q.infer && !a[id] && q.infer(a)) a[id] = q.infer(a);
    });
    const ok = USES[a.use] && pathFor(a).every((id) => optionOf(id, a));
    return ok ? a : null;
  }

  function resultUrl(a) {
    const u = new URL(window.location.href);
    u.search = '';
    u.hash = '';
    // Answers are plain words, colons and commas, so the link stays readable unencoded
    return u.toString() + '?a=' + encodeAnswers(a);
  }

  // Scoring and wording, exposed for _tools/simulate-finder.js and for debugging in the console
  window.lytesFinder = { USES, QUESTIONS, PRIORITIES, pathFor, rank, summary, headline, reasons, tradeoff, bandRanges, encodeAnswers, decodeAnswers, lineKey, describeLine, flavourText };
  if (!root) return;

  // ─── Elements ───────────────────────────────────────────────────────────────
  const $ = (sel, el = root) => el.querySelector(sel);
  const dialog = $('[data-finder-dialog]');
  const qTitle = $('[data-finder-question]');
  const qHint = $('[data-finder-hint]');
  const qNote = $('[data-finder-note]');
  const qOptions = $('[data-finder-options]');
  const qStep = $('[data-finder-step]');
  const progress = $('[data-finder-progress]');
  const backBtn = $('[data-finder-back]');
  const continueBtn = $('[data-finder-continue]');
  const startView = $('[data-finder-start]');
  const resultView = $('[data-finder-result]');
  const welcome = $('[data-finder-welcome]');
  const icons = {};
  root.querySelectorAll('template[data-icon]').forEach((t) => (icons[t.dataset.icon] = t.innerHTML));

  // ─── Quiz flow ──────────────────────────────────────────────────────────────
  function openQuiz(startAt = 0) {
    current = startAt;
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('lytes-finder-open');
    renderQuestion();
    loadProducts().catch(() => {});
  }

  function closeQuiz() {
    if (dialog.open) dialog.close();
  }

  dialog.addEventListener('close', () => document.documentElement.classList.remove('lytes-finder-open'));

  function renderQuestion() {
    const path = pathFor(answers);
    const id = path[current];
    const q = QUESTIONS[id];
    const opts = q.options(answers);

    root.querySelectorAll('[data-stage]').forEach((el) => {
      const n = Number(el.dataset.stage);
      el.classList.toggle('is-current', n === q.stage);
      el.classList.toggle('is-done', n < q.stage);
    });
    // The path length is only known once the first answer picks a track
    qStep.textContent = answers.use ? `Question ${current + 1} of ${path.length}` : `Question ${current + 1}`;
    progress.style.width = Math.round((current / path.length) * 100) + '%';
    qTitle.textContent = q.title;
    qHint.textContent = q.hint || '';
    qHint.hidden = !q.hint;
    const note = q.inferredNote ? q.inferredNote(answers) : '';
    qNote.textContent = note;
    qNote.hidden = !note;

    qOptions.className = 'lytes-finder__options' + (q.layout === 'icons' ? ' lytes-finder__options--icons' : '') + (q.multi ? ' lytes-finder__options--ranked' : '');
    qOptions.setAttribute('aria-labelledby', qTitle.id);
    qOptions.setAttribute('role', q.multi ? 'group' : 'radiogroup');
    const picks = q.multi ? ranksOf(answers) : [];
    qOptions.innerHTML = opts
      .map((o, i) => {
        if (q.multi) {
          // Ranked choice: the badge shows the pick's order (1, 2, 3)
          const n = picks.indexOf(o.value) + 1;
          return `<button type="button" class="lytes-finder__option" role="checkbox" aria-checked="${n > 0}" tabindex="${i === 0 ? 0 : -1}" data-value="${esc(o.value)}"${n ? ` aria-description="Choice ${n}"` : ''}>
            <span class="lytes-finder__option-check lytes-finder__option-rank" aria-hidden="true">${n || ''}</span>
            <span class="lytes-finder__option-text"><span class="lytes-finder__option-label">${esc(o.label)}</span>${o.sub ? `<span class="lytes-finder__option-sub">${esc(o.sub)}</span>` : ''}</span>
          </button>`;
        }
        const checked = answers[id] === o.value;
        const tab = checked || (!answers[id] && i === 0) ? 0 : -1;
        return `<button type="button" class="lytes-finder__option" role="radio" aria-checked="${checked}" tabindex="${tab}" data-value="${esc(o.value)}">
          ${o.icon ? `<span class="lytes-finder__option-icon">${icons[o.icon] || ''}</span>` : '<span class="lytes-finder__option-check" aria-hidden="true">' + (icons.check || '') + '</span>'}
          <span class="lytes-finder__option-text"><span class="lytes-finder__option-label">${esc(o.label)}</span>${o.sub ? `<span class="lytes-finder__option-sub">${esc(o.sub)}</span>` : ''}</span>
        </button>`;
      })
      .join('');

    backBtn.hidden = current === 0;
    continueBtn.hidden = q.multi ? !picks.length : !answers[id];
    continueBtn.textContent = current === path.length - 1 ? 'See my match' : 'Continue';
    requestAnimationFrame(() => qTitle.focus({ preventScroll: true }));
  }

  function choose(value) {
    const path = pathFor(answers);
    const id = path[current];
    if (QUESTIONS[id].multi) {
      // Tap to add in order, tap again to remove; up to three. Continue moves on.
      let picks = ranksOf(answers);
      if (picks.includes(value)) picks = picks.filter((k) => k !== value);
      else if (picks.length < MAX_PRIORITIES) picks.push(value);
      else {
        qHint.textContent = 'You’ve picked three. Tap one to remove it first.';
        qHint.hidden = false;
        return;
      }
      if (picks.length) answers[id] = picks.join('.');
      else delete answers[id];
      const focused = document.activeElement && document.activeElement.dataset ? document.activeElement.dataset.value : null;
      renderQuestion();
      const again = focused && qOptions.querySelector(`[data-value="${focused}"]`);
      if (again) requestAnimationFrame(() => again.focus({ preventScroll: true }));
      return;
    }
    const before = answers.use;
    answers[id] = value;
    // A new routing answer resets the answers that depend on it
    if (id === 'use' && before !== value) {
      ['sweat', 'exposure', 'keto', 'taste', 'priority'].forEach((k) => delete answers[k]);
      if (USES[value].track === 'keto' && (answers.sweet === 'some' || answers.sweet === 'any')) delete answers.sweet;
      const inferred = QUESTIONS.exposure.infer(answers);
      if (inferred) answers.exposure = inferred;
    }
    track('electrolyte_quiz_question_answered', { question_id: id, answer_value: value, step: current + 1 });
    qOptions.querySelectorAll('.lytes-finder__option').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.value === value)));
    // Short pause so the tick registers before moving on
    setTimeout(next, 220);
  }

  function next() {
    const path = pathFor(answers);
    if (current < path.length - 1) {
      current += 1;
      renderQuestion();
    } else {
      finish();
    }
  }

  qOptions.addEventListener('click', (e) => {
    const btn = e.target.closest('.lytes-finder__option');
    if (btn) choose(btn.dataset.value);
  });

  // Arrow keys move between options like a native radio group
  qOptions.addEventListener('keydown', (e) => {
    const btns = Array.from(qOptions.querySelectorAll('.lytes-finder__option'));
    const i = btns.indexOf(document.activeElement);
    if (i < 0) return;
    const move = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
    if (!move) return;
    e.preventDefault();
    const n = btns[(i + move + btns.length) % btns.length];
    btns.forEach((b) => (b.tabIndex = -1));
    n.tabIndex = 0;
    n.focus();
  });

  backBtn.addEventListener('click', () => {
    if (current > 0) {
      track('electrolyte_quiz_back_clicked', { step: current + 1 });
      current -= 1;
      renderQuestion();
    }
  });
  continueBtn.addEventListener('click', () => {
    const id = pathFor(answers)[current];
    // Ranked picks are recorded when the shopper moves on, not on every tap
    if (QUESTIONS[id] && QUESTIONS[id].multi) track('electrolyte_quiz_question_answered', { question_id: id, answer_value: answers[id], step: current + 1 });
    next();
  });
  $('[data-finder-close]').addEventListener('click', closeQuiz);

  function finish() {
    progress.style.width = '100%';
    root.querySelectorAll('[data-stage]').forEach((el) => {
      el.classList.toggle('is-current', el.dataset.stage === '3');
      el.classList.toggle('is-done', el.dataset.stage !== '3');
    });
    qStep.textContent = 'Finding your match';
    qTitle.textContent = 'Checking every product against your answers…';
    qHint.hidden = true;
    qNote.hidden = true;
    qOptions.innerHTML = '';
    backBtn.hidden = true;
    continueBtn.hidden = true;
    track('electrolyte_quiz_completed', { answers: { ...answers }, path: pathFor(answers).join('>') });
    const minWait = new Promise((r) => setTimeout(r, 700));
    Promise.all([loadProducts(), minWait])
      .then(([products]) => {
        closeQuiz();
        showResult(products, answers, { fresh: true });
      })
      .catch((err) => {
        console.warn('[finder] could not load or score products', err);
        qTitle.textContent = 'We couldn’t load the product list. Check your connection and try again.';
        continueBtn.hidden = false;
        continueBtn.textContent = 'Try again';
        continueBtn.onclick = () => {
          continueBtn.onclick = null;
          finish();
        };
      });
  }

  // ─── Result ─────────────────────────────────────────────────────────────────
  function stars(rating) {
    const r = Number(rating) || 0;
    let html = '';
    for (let i = 1; i <= 5; i++) html += `<span class="lytes-stars__star${i > Math.round(r) ? ' is-off' : ''}">${icons.star || ''}</span>`;
    return `<span class="lytes-stars" role="img" aria-label="${r.toFixed(1)} out of 5 stars">${html}</span>`;
  }

  // Per serve facts are the same across a line's flavours; price per serve varies by pack, so it sits in the price row
  function facts(p) {
    const rows = [
      ['Sodium', mg(p.sodium_mg)],
      ['Potassium', mg(p.potassium_mg)],
      ['Magnesium', mg(p.magnesium_mg)],
      ['Calcium', mg(p.calcium_mg)],
      ['Sugar', p.sugar_profile || '—'],
      ['Sweetener', (p.sweeteners || []).join(', ') || '—'],
    ];
    return `<dl class="lytes-finder__facts">${rows.map((r) => `<div><dt>${r[0]}</dt><dd>${esc(r[1])}</dd></div>`).join('')}</dl>`;
  }

  function priceLine(p) {
    const pps = perServe(p);
    const sale = p.compare_at && p.compare_at > p.price ? ` <s>${money(p.compare_at)}</s>` : '';
    return `<span class="lytes-finder__price">${money(p.price)}${sale}</span>${pps ? ` <span class="lytes-finder__pps">${money(pps)} per serve${p.serves ? ` · ${p.serves} serves` : ''}</span>` : ''}`;
  }

  function rating(p) {
    return Number(p.rating) > 0 && p.reviews > 0 ? `<a class="lytes-finder__rating" href="${esc(p.url)}#judgeme_product_reviews">${stars(p.rating)}<span>${Number(p.rating).toFixed(1)} (${p.reviews})</span></a>` : '';
  }

  function buyButton(p, label, cls = 'button') {
    if (p.variants > 1) return `<a class="${cls}" href="${esc(p.url)}" data-finder-product>${esc('Choose options')}</a>`;
    return `<button type="button" class="${cls}" data-finder-add="${p.variant_id}" data-handle="${esc(p.handle)}">${esc(label)}</button>`;
  }

  // ─── Product line cards ──────────────────────────────────────────────────────
  function lineRating(l) {
    return l.rating > 0 && l.reviews > 0 ? `<a class="lytes-finder__rating" href="${esc(l.url)}">${stars(l.rating)}<span>${l.rating.toFixed(1)} (${l.reviews})</span></a>` : '';
  }

  function flavourText(l) {
    const n = l.flavours.length;
    if (!n) return l.packs ? 'Comes as a variety pack' : '';
    const shown = n > 6 ? l.flavours.slice(0, 5) : l.flavours;
    const more = n > 6 ? `, plus ${n - 5} more` : '';
    const one = l.flavours[0];
    const lead = n === 1 ? (/^unflavou?red$/i.test(one) ? 'Unflavoured' : `One flavour: ${one}`) : `Comes in ${n} flavours: ${shown.join(', ')}${more}`;
    return lead + (l.packs ? `, and ${l.packs > 1 ? 'variety packs' : 'a variety pack'}` : '');
  }

  function lineSummary(l) {
    const bits = [];
    const f = flavourText(l);
    if (f) bits.push(`<p class="lytes-finder__line-flavours">${esc(f)}</p>`);
    if (l.formats.length) bits.push(`<p class="lytes-finder__line-formats">Comes as ${esc(listJoin(l.formats))}</p>`);
    return bits.join('');
  }

  function linePrice(l) {
    if (l.options.length === 1) return `<span class="lytes-finder__price">${money(l.minPrice)}</span>${l.minPps ? ` <span class="lytes-finder__pps">${money(l.minPps)} per serve</span>` : ''}`;
    return `<span class="lytes-finder__price">From ${money(l.minPps)} per serve</span>${l.minPrice ? ` <span class="lytes-finder__pps">Packs from ${money(l.minPrice)}</span>` : ''}`;
  }

  // Every in-stock flavour and pack size of the line, variety packs first so nobody has to guess a flavour
  function chooser(l, cls = '') {
    const count = l.options.length;
    // Only one product in the line: nothing to choose, so add it straight away
    if (count === 1) return `${buyButton(l.options[0].p, 'Add to cart', cls ? 'button button-secondary' : 'button')}<p class="lytes-finder__status" data-finder-cart-status role="status"></p>`;
    const intro = l.packs && l.flavours.length > 1 ? `<p class="lytes-finder__choose-note">Not sure which flavour? A variety pack lets you try several.</p>` : '';
    const what = l.flavours.length > 1 ? 'flavour' : 'pack';
    // One row per flavour (or variety pack), with each pack size under it
    const groups = new Map();
    for (const o of l.options) {
      const k = o.label.toLowerCase();
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(o);
    }
    const sizeRow = (o) => `<li>
            <a class="lytes-finder__choose-size" href="${esc(o.p.url)}" data-finder-product>${esc(o.size || 'Buy')}</a>
            <span class="lytes-finder__choose-price">${money(o.p.price)}${perServe(o.p) ? `<span>${money(perServe(o.p))}/serve</span>` : ''}</span>
            ${buyButton(o.p, 'Add', 'button button-secondary lytes-finder__choose-add')}
          </li>`;
    return `<details class="lytes-finder__choose ${cls}" data-finder-choose>
      <summary class="button${cls ? ' button-secondary' : ''}">Choose your ${what} (${groups.size > 1 ? groups.size : count}) ${icons['chevron-down'] || ''}</summary>
      ${intro}
      <ul class="lytes-finder__choose-list" role="list">
        ${[...groups.values()]
          .map(
            (g) => `<li class="lytes-finder__choose-group">
          <p class="lytes-finder__choose-name">${g[0].pack ? '<span class="lytes-finder__choose-tag">Variety</span>' : ''}${esc(g[0].label)}</p>
          <ul class="lytes-finder__choose-sizes" role="list">
          ${g
            .slice()
            .sort((x, y) => (Number(x.p.serves) || 0) - (Number(y.p.serves) || 0))
            .map(sizeRow)
            .join('')}
          </ul>
        </li>`
          )
          .join('')}
      </ul>
      <p class="lytes-finder__status" data-finder-cart-status role="status"></p>
    </details>`;
  }

  function topCard(x, why) {
    const p = x.p;
    const l = x.line;
    return `<article class="lytes-finder__top">
      <a class="lytes-finder__top-media" href="${esc(l.url)}" data-finder-product tabindex="-1" aria-hidden="true">${l.image ? `<img src="${esc(l.image)}" alt="" width="480" height="480" loading="lazy">` : ''}</a>
      <div class="lytes-finder__top-body">
        <p class="lytes-finder__vendor">${esc(l.vendor)}</p>
        <h3 class="lytes-finder__title"><a href="${esc(l.url)}" data-finder-product>${esc(l.name)}</a></h3>
        ${lineRating(l)}
        <p class="lytes-finder__price-row">${linePrice(l)}</p>
        ${lineSummary(l)}
        <ul class="lytes-finder__reasons" role="list">${why.map((r) => `<li>${icons.check || ''}<span>${esc(r)}</span></li>`).join('')}</ul>
        ${facts(p)}
        <div class="lytes-finder__actions">
          ${chooser(l)}
          ${p.brand_url ? `<a class="lytes-link" href="${esc(p.brand_url)}">All ${esc(p.vendor)}</a>` : ''}
        </div>
      </div>
    </article>`;
  }

  function altCard(x, top, a) {
    const l = x.line;
    return `<article class="lytes-finder__alt">
      <a class="lytes-finder__alt-media" href="${esc(l.url)}" data-finder-product tabindex="-1" aria-hidden="true">${l.image ? `<img src="${esc(l.image)}" alt="" width="240" height="240" loading="lazy">` : ''}</a>
      <div class="lytes-finder__alt-body">
        <p class="lytes-finder__vendor">${esc(l.vendor)}</p>
        <h4 class="lytes-finder__alt-title"><a href="${esc(l.url)}" data-finder-product>${esc(l.name)}</a></h4>
        <p class="lytes-finder__tradeoff">${esc(tradeoff(x, top, a))}</p>
        <p class="lytes-finder__price-row">${linePrice(l)}</p>
        ${lineSummary(l)}
        ${chooser(l, 'lytes-finder__choose--alt')}
      </div>
    </article>`;
  }

  function compareTable(list) {
    // Nutrition is per serve and the same across a line's flavours; prices are the line's lowest
    const rows = [
      ['Sodium', (x) => mg(x.p.sodium_mg)],
      ['Potassium', (x) => mg(x.p.potassium_mg)],
      ['Magnesium', (x) => mg(x.p.magnesium_mg)],
      ['Sugar', (x) => x.p.sugar_profile || '—'],
      ['Sweetener', (x) => (x.p.sweeteners || []).join(', ') || '—'],
      ['Flavours', (x) => (x.line.flavours.length ? String(x.line.flavours.length) : '—') + (x.line.packs ? ' + variety' : '')],
      ['Comes as', (x) => listJoin(x.line.formats) || '—'],
      ['Per serve', (x) => (x.line.minPps ? 'From ' + money(x.line.minPps) : '—')],
      ['Packs from', (x) => money(x.line.minPrice) || '—'],
      ['Australian made', (x) => (auStatus(x.p) ? AU_LABEL[auStatus(x.p)] : 'No')],
      ['Rating', (x) => (x.line.reviews ? `${x.line.rating.toFixed(1)} (${x.line.reviews})` : 'No reviews yet')],
    ];
    return `<div class="lytes-finder__table-wrap"><table class="lytes-finder__table">
      <thead><tr><th scope="col"><span class="visually-hidden">Detail</span></th>${list.map((x, i) => `<th scope="col">${i === 0 ? '<span class="lytes-finder__badge">Your match</span>' : ''}<a href="${esc(x.line.url)}" data-finder-product>${esc(x.line.name)}</a></th>`).join('')}</tr></thead>
      <tbody>${rows.map((r) => `<tr><th scope="row">${r[0]}</th>${list.map((x) => `<td>${esc(r[1](x))}</td>`).join('')}</tr>`).join('')}</tbody>
    </table></div>`;
  }

  function showResult(products, a, opts = {}) {
    const result = rank(products, a);
    const ranges = bandRanges(products);
    const out = $('[data-finder-result-body]');
    lastResult = { a: { ...a }, result };

    if (!result.byBrand.length) {
      out.innerHTML = `<div class="lytes-finder__empty"><h2 class="lytes-finder__headline">No product fits all of that right now</h2>
        <p>${esc(summary(a, ranges))} Nothing in stock ticks every box at the moment, usually because of the sweetener choice. Try a different answer for sugar and sweeteners.</p></div>`;
    } else {
      const top = result.byBrand[0];
      const alts = result.byBrand.slice(1, 3);
      const close = alts[0] && top.total - alts[0].total <= 0.5;
      const why = reasons(top, a, result.field);
      out.innerHTML = `
        <p class="lytes-eyebrow">Your match</p>
        <h2 class="lytes-finder__headline" tabindex="-1">${esc(headline(top, a))}</h2>
        <p class="lytes-finder__summary">${esc(summary(a, ranges))}</p>
        ${topCard(top, why)}
        ${close ? `<p class="lytes-finder__close-call">${icons.scale || ''}<span><strong>It’s close.</strong> ${esc(alts[0].line.name)} fits your answers almost as well. The table below shows the differences.</span></p>` : ''}
        ${alts.length ? `<h3 class="lytes-finder__subhead">Also worth a look</h3><div class="lytes-finder__alts">${alts.map((x) => altCard(x, top, a)).join('')}</div>` : ''}
        ${alts.length ? `<details class="lytes-finder__compare" ${close ? 'open' : ''} data-finder-compare><summary>Compare your top ${alts.length + 1} side by side ${icons['chevron-down'] || ''}</summary>${compareTable([top, ...alts])}</details>` : ''}
        <p class="lytes-finder__pool">Matched from ${result.poolSize} in-stock electrolytes that fit your answers${result.excluded.length ? `, out of ${products.length} we checked` : ''}. Brands don’t pay to be recommended.</p>`;
      store.set({ v: 2, answers: a, at: new Date().toISOString(), title: top.line.name, handle: top.p.handle });
      track('electrolyte_quiz_result_viewed', { first_line: top.line.name, second_line: alts[0] && alts[0].line.name, third_line: alts[1] && alts[1].line.name, first_place: top.p.id, second_place: alts[0] && alts[0].p.id, third_place: alts[1] && alts[1].p.id, close_call: !!close, source: opts.source || 'quiz' });
    }

    $('[data-finder-share-url]').value = resultUrl(a);
    if (opts.fresh) history.replaceState(null, '', resultUrl(a));
    startView.hidden = true;
    welcome.hidden = true;
    resultView.hidden = false;
    const emailStatus = $('[data-finder-email-status]');
    if (emailStatus) emailStatus.textContent = '';
    const heading = out.querySelector('.lytes-finder__headline');
    resultView.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    if (heading) heading.focus({ preventScroll: true });
  }

  // ─── Result actions: add to cart, share, retake, email ─────────────────────
  root.addEventListener('click', async (e) => {
    const add = e.target.closest('[data-finder-add]');
    if (add) {
      e.preventDefault();
      const id = Number(add.dataset.finderAdd);
      const status = add.closest('article').querySelector('[data-finder-cart-status]') || $('[data-finder-result-body] [data-finder-cart-status]');
      add.disabled = true;
      add.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch((window.Theme?.routes?.cart_add_url || '/cart/add') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.description || body.message || 'Could not add to cart');
        // Orders made from a quiz result carry the quiz session, for attribution
        fetch((window.Theme?.routes?.cart_update_url || '/cart/update') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _electrolyte_finder: `${QUIZ_VERSION}|${sessionId}|${encodeAnswers(answers)}` } }),
        }).catch(() => {});
        if (window.lytesCartRefresh) window.lytesCartRefresh([{ id, quantity: 1 }]);
        if (status) status.textContent = 'Added to your cart.';
        track('electrolyte_quiz_add_to_cart', { variant_id: id, handle: add.dataset.handle });
      } catch (err) {
        if (status) status.textContent = String(err.message || err);
      } finally {
        add.disabled = false;
        add.removeAttribute('aria-busy');
      }
      return;
    }
    const link = e.target.closest('[data-finder-product]');
    if (link) track('electrolyte_quiz_product_clicked', { url: link.getAttribute('href') });
  });

  root.addEventListener('toggle', (e) => {
    if (e.target.matches('[data-finder-compare]') && e.target.open) track('electrolyte_quiz_compare_clicked', {});
  }, true);

  $('[data-finder-copy]').addEventListener('click', async () => {
    const input = $('[data-finder-share-url]');
    const status = $('[data-finder-copy-status]');
    try {
      if (navigator.share && matchMedia('(pointer: coarse)').matches) {
        await navigator.share({ title: 'My electrolyte match', url: input.value });
      } else {
        await navigator.clipboard.writeText(input.value);
        status.textContent = 'Link copied.';
      }
    } catch (e) {
      input.select();
      status.textContent = 'Select the link and copy it.';
    }
    track('electrolyte_quiz_shared', {});
  });

  function retake() {
    track('electrolyte_quiz_retake', {});
    answers = {};
    history.replaceState(null, '', window.location.pathname);
    resultView.hidden = true;
    startView.hidden = false;
    openQuiz(0);
  }
  root.querySelectorAll('[data-finder-retake]').forEach((b) => b.addEventListener('click', retake));
  $('[data-finder-edit]').addEventListener('click', () => {
    answers = { ...(lastResult ? lastResult.a : answers) };
    openQuiz(0);
  });

  // Email my results: always logs a Klaviyo event the results flow sends from; only joins
  // the marketing list when the shopper ticks the separate box.
  const emailForm = $('[data-finder-email]');
  if (emailForm) {
    emailForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = emailForm.email.value.trim();
      const consent = emailForm.marketing.checked;
      const status = $('[data-finder-email-status]');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        status.textContent = 'Enter an email address like name@example.com.';
        emailForm.email.focus();
        return;
      }
      if (!lastResult || !lastResult.result.byBrand.length) return;
      const btn = emailForm.querySelector('button[type="submit"]');
      btn.disabled = true;
      status.textContent = 'Sending…';
      const requests = [klaviyoEvent(email)];
      if (consent && CONFIG.klaviyo_list) requests.push(klaviyoSubscribe(email));
      Promise.all(requests)
        .then((responses) => {
          if (responses.some((r) => !r.ok)) throw new Error('Klaviyo ' + responses.map((r) => r.status).join(','));
          status.textContent = 'Sent. Your match is on its way to ' + email + '.';
          emailForm.reset();
          track('electrolyte_quiz_email_capture_attempted', { marketing_consent: consent, success: true });
        })
        .catch((err) => {
          console.warn('[finder] email', err);
          status.textContent = 'That didn’t send. Check the address and try again.';
          track('electrolyte_quiz_email_capture_attempted', { marketing_consent: consent, success: false });
        })
        .finally(() => (btn.disabled = false));
    });
  }

  function absolute(url) {
    if (!url) return '';
    if (url.startsWith('//')) return 'https:' + url;
    return new URL(url, window.location.origin).toString();
  }

  function klaviyoEvent(email) {
    const { a, result } = lastResult;
    const top = result.byBrand[0];
    const alts = result.byBrand.slice(1, 3);
    const ranges = bandRanges(result.scored.map((x) => x.p));
    const item = (x) => ({ title: x.line.name, vendor: x.p.vendor, url: absolute(x.line.url), image: absolute(x.line.image), price: 'from ' + money(x.line.minPrice), per_serve: 'from ' + money(x.line.minPps), flavours: flavourText(x.line), formats: listJoin(x.line.formats), sodium: mg(x.p.sodium_mg) });
    const properties = {
      quiz_version: QUIZ_VERSION,
      session_id: sessionId,
      answers: encodeAnswers(a),
      use: USES[a.use].label,
      headline: headline(top, a),
      summary: summary(a, ranges),
      reasons: reasons(top, a, result.field),
      match: item(top),
      alternatives: alts.map((x) => ({ ...item(x), tradeoff: tradeoff(x, top, a) })),
      results_url: resultUrl(a),
      $value: top.p.price,
    };
    return fetch('https://a.klaviyo.com/client/events?company_id=' + encodeURIComponent(CONFIG.klaviyo_key), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: '2025-07-15' },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties,
            metric: { data: { type: 'metric', attributes: { name: 'Requested Electrolyte Match' } } },
            profile: { data: { type: 'profile', attributes: { email, properties: { electrolyte_finder_use: USES[a.use].label, electrolyte_finder_match: top.line.name, electrolyte_finder_date: new Date().toISOString().slice(0, 10) } } } },
          },
        },
      }),
    });
  }

  function klaviyoSubscribe(email) {
    return fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(CONFIG.klaviyo_key), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: '2025-07-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Electrolyte Finder',
            profile: { data: { type: 'profile', attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } },
          },
          relationships: { list: { data: { type: 'list', id: CONFIG.klaviyo_list } } },
        },
      }),
    });
  }

  // ─── Start: page tiles, deep links, shared links, welcome back ─────────────
  root.querySelectorAll('[data-finder-use]').forEach((btn) =>
    btn.addEventListener('click', () => {
      answers = { use: btn.dataset.finderUse };
      const inferred = QUESTIONS.exposure.infer(answers);
      if (inferred) answers.exposure = inferred;
      track('electrolyte_quiz_started', { entry: 'page_tile', use: answers.use });
      track('electrolyte_quiz_question_answered', { question_id: 'use', answer_value: answers.use, step: 1 });
      openQuiz(1);
    })
  );
  root.querySelectorAll('[data-finder-open]').forEach((btn) =>
    btn.addEventListener('click', () => {
      answers = {};
      track('electrolyte_quiz_started', { entry: 'button' });
      openQuiz(0);
    })
  );

  const params = new URLSearchParams(window.location.search);
  const shared = params.get('a') ? decodeAnswers(params.get('a')) : null;
  const saved = store.get();
  // Saved answers go through the decoder so older results (separate Australian made) still load
  const savedAnswers = saved ? decodeAnswers(Object.entries(saved.answers).map(([k, v]) => k + ':' + v).join(',')) : null;

  if (shared) {
    answers = { ...shared };
    loadProducts()
      .then((products) => showResult(products, shared, { source: 'link' }))
      .catch(() => {});
  } else if (params.has('use') && USES[params.get('use')]) {
    // Entry points elsewhere on the site link here with ?use=keto and start at question 2
    answers = { use: params.get('use') };
    const inferred = QUESTIONS.exposure.infer(answers);
    if (inferred) answers.exposure = inferred;
    track('electrolyte_quiz_started', { entry: 'link', use: answers.use });
    openQuiz(1);
  } else if (params.has('start') || window.location.hash === '#quiz') {
    track('electrolyte_quiz_started', { entry: 'link' });
    openQuiz(0);
  } else if (savedAnswers) {
    $('[data-finder-welcome-title]').textContent = saved.title || 'your match';
    const when = new Date(saved.at);
    if (!isNaN(when)) $('[data-finder-welcome-date]').textContent = ' on ' + when.toLocaleDateString('en-AU', { day: 'numeric', month: 'long' });
    welcome.hidden = false;
    $('[data-finder-welcome-show]').addEventListener('click', () => {
      answers = { ...savedAnswers };
      loadProducts().then((products) => showResult(products, answers, { source: 'saved' }));
    });
  }
})();
